import { Dropdown, Icon } from "semantic-ui-react";

import React, { useState } from "react";
import { useTranslation } from "react-i18next";

import { db } from "astrid-firebase";
import distributionStatuses from "astrid-firestore/src/api/distribution/constants/distributionStatuses";
import useCollectionData from "astrid-firestore/src/hooks/useCollectionData";
import useDocumentData from "astrid-firestore/src/hooks/useDocumentData";
import useBucket from "astrid-hooks/src/useBucket";

import PipelineStatusLabel from "../../../../distribution/components/DistributionStatus/PipelineStatusLabel";
import distributionStatusColors from "../../../../distribution/utils/distributionStatusColors";
import CreateExportForm from "../../../../exports/components/CreateExportForm/CreateExportForm";
import { getPipelineProgressPercentage } from "../../../../pipelines/utils/pipelineProgress";
import Button from "../../../../ui/components/Buttons/Button";
import DropdownMenu from "../../../../ui/components/DropdownMenu/DropdownMenu";
import FileDownloadModal from "../../../../ui/components/FileDownload/FileDownloadModal";
import LoadingContext from "../../../../ui/components/LoadingContext/LoadingContext";
import ModalPrompt from "../../../../ui/components/ModalPrompt/ModalPrompt";
import ProgressLabel from "../../../../ui/components/ProgressLabel/ProgressLabel";

export default function ArticleExports({ article, selectedArtifact, publisher }) {
	const { t } = useTranslation();

	const [downloadModal, setDownloadModal] = useState(false);
	const [createOpen, setCreateOpen] = useState(false);
	const [shareOpen, setShareOpen] = useState(false);
	const [shareLoadingId, setShareLoadingId] = useState(null);
	const [copiedExportId, setCopiedExportId] = useState(null);
	const { downloadFolder, getShareableUrl } = useBucket("earselect-exports");

	const [allExports, loading, error] = useCollectionData(
		selectedArtifact &&
			db
				.collection("exports")
				.where("artifact.id", "==", selectedArtifact.id)
				.where("deleted", "==", false)
				.where("created", ">=", selectedArtifact.updated),
	);

	const latestUniqueExports = allExports && [
		...allExports
			.reduce((map, exp) => {
				if (!map.has(exp.name) || exp.created > map.get(exp.name).created) {
					map.set(exp.name, exp);
				}
				return map;
			}, new Map())
			.values(),
	];

	const exportCount = latestUniqueExports?.length;
	const runningExport = latestUniqueExports?.find((exp) =>
		[
			distributionStatuses.QUEUED,
			distributionStatuses.PENDING,
			distributionStatuses.INITIALIZED,
			distributionStatuses.RUNNING,
		].includes(exp.status),
	);

	const [progressDoc] = useDocumentData(runningExport && runningExport.ref.collection("progress").doc("progressDoc"));

	const percentage = runningExport ? getPipelineProgressPercentage(progressDoc?.progress) || 1 : null;

	const copyExportLinks = async (theExport) => {
		setShareLoadingId(theExport.id);
		try {
			const blob = getShareableUrl(theExport.id).then((url) => new Blob([url], { type: "text/plain" }));
			await navigator.clipboard.write([new ClipboardItem({ "text/plain": blob })]);
			setCopiedExportId(theExport.id);
			setTimeout(() => setCopiedExportId(null), 2000);
		} catch (err) {
			console.error(err);
		} finally {
			setShareLoadingId(null);
		}
	};

	return (
		<>
			<DropdownMenu
				direction="right"
				icon={
					<ProgressLabel
						basic={!percentage}
						color={distributionStatusColors?.[runningExport?.status]}
						percent={percentage}
						active={percentage}
						style={{ cursor: "pointer" }}
					>
						{percentage ? <span>{`${percentage} % `}</span> : <Icon name="download" />}
						{t("exports", "Exports")}
						{exportCount > 0 && (
							<span style={{ marginLeft: "0.4em", opacity: 0.6 }}>{`(${exportCount})`}</span>
						)}
					</ProgressLabel>
				}
				options={
					selectedArtifact
						? [
								{ type: "divider" },
								{
									text: t("share", "Share"),
									icon: "share alternate",
									hidden: !exportCount,
									onClick: () => setShareOpen(true),
								},
								{
									text: t("createExport", "Create export"),
									icon: "plus",
									onClick: () => setCreateOpen(true),
								},
						  ]
						: undefined
				}
			>
				<LoadingContext data={latestUniqueExports} loading={loading} error={error}>
					{latestUniqueExports?.map((theExport) => (
						<Dropdown.Item
							key={theExport.id}
							onClick={() => {
								if (theExport.status === distributionStatuses.COMPLETED) {
									setDownloadModal(theExport);
								}
							}}
						>
							<div style={{ display: "flex", alignItems: "center", gap: "0.6em" }}>
								<PipelineStatusLabel distribution={theExport} />
								<span>{theExport.name}</span>
							</div>
						</Dropdown.Item>
					))}
				</LoadingContext>
			</DropdownMenu>

			{downloadModal && (
				<FileDownloadModal
					filesOrFolder={downloadModal.id}
					downloadFn={downloadFolder}
					infoText={downloadModal.name}
					onClose={() => setDownloadModal(false)}
				/>
			)}

			{createOpen && (
				<CreateExportForm
					article={article}
					artifact={selectedArtifact}
					publisher={publisher}
					onClose={() => setCreateOpen(false)}
				/>
			)}

			{shareOpen && (
				<ModalPrompt
					header={t("shareLinks", "Share links")}
					icon="share alternate"
					size="tiny"
					onClose={() => setShareOpen(false)}
					showConfirmButton={false}
				>
					<div style={{ display: "flex", flexDirection: "column", gap: "0.5em" }}>
						{latestUniqueExports
							?.filter((exp) => exp.status === distributionStatuses.COMPLETED)
							.map((theExport) => (
								<div key={theExport.id} style={{ display: "flex", alignItems: "center", gap: "0.6em" }}>
									<span style={{ flex: 1 }}>{theExport.name}</span>
									<Button
										icon={
											shareLoadingId === theExport.id
												? "spinner"
												: copiedExportId === theExport.id
												? "check"
												: "copy"
										}
										loading={shareLoadingId === theExport.id}
										size="tiny"
										color={copiedExportId === theExport.id ? "green" : undefined}
										onClick={() => copyExportLinks(theExport)}
									/>
								</div>
							))}
					</div>
				</ModalPrompt>
			)}
		</>
	);
}
